// 차수별현황 – 매수 등록 팝업
// props: data { account_name, stock_name, ticker, current_price, target_profit_rate }
//        onClose: 닫기 콜백
//        onSaved: 저장 성공 후 콜백
import React, { useState, useEffect } from 'react';
import { createBuy, fetchNextTurnNo } from '../../api/buy';

const todayStr = () => new Date().toISOString().substring(0, 10);

const fAmt = (v) => {
  if (v == null || isNaN(Number(v))) return '';
  return Math.round(Number(v)).toLocaleString('ko-KR');
};

const TurnBuyPopup = ({ data, onClose, onSaved }) => {
  const [buyDate,    setBuyDate]    = useState(todayStr());
  const [turnNo,     setTurnNo]     = useState(null);   // 서버에서 자동계산
  const [buyPrice,   setBuyPrice]   = useState(data.current_price != null ? String(data.current_price) : '');
  const [quantity,   setQuantity]   = useState('');
  const [targetRate, setTargetRate] = useState(
    data.target_profit_rate != null ? String(data.target_profit_rate) : ''
  );
  const [memo,    setMemo]    = useState('');
  const [saving,  setSaving]  = useState(false);
  const [loadingTurn, setLoadingTurn] = useState(true);
  const [error,   setError]   = useState('');

  // ESC 키로 닫기
  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  // 차수 자동계산
  useEffect(() => {
    setLoadingTurn(true);
    fetchNextTurnNo(data.account_name, data.ticker)
      .then(n => { setTurnNo(n); setLoadingTurn(false); })
      .catch(() => { setLoadingTurn(false); });
  }, [data.account_name, data.ticker]);

  const bp  = Number(buyPrice)  || 0;
  const qty = Number(quantity)  || 0;
  const buyAmt = bp * qty;

  const handleSave = async () => {
    if (!buyDate)   return setError('매수일자를 입력하세요.');
    if (!(bp > 0))  return setError('매수단가를 입력하세요.');
    if (!(qty > 0)) return setError('수량을 입력하세요.');
    setSaving(true);
    setError('');
    try {
      await createBuy({
        account_name:       data.account_name,
        ticker:             data.ticker,
        buy_date:           buyDate,
        buy_price:          bp,
        quantity:           qty,
        target_profit_rate: targetRate !== '' ? Number(targetRate) : undefined,
        memo:               memo || undefined,
      });
      onSaved();
    } catch (e) {
      setError(e.response?.data?.detail || '저장에 실패했습니다.');
    } finally {
      setSaving(false);
    }
  };

  const turnLabel = loadingTurn ? '계산 중...' : turnNo != null ? `${turnNo}차` : '-';

  return (
    <div className="popup-overlay" onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="popup-box">
        <div className="popup-title buy-title">
          매수 등록 &mdash; {data.stock_name || data.ticker} {!loadingTurn && turnNo != null ? `${turnNo}차` : ''}
        </div>

        <div className="popup-body">
          <table className="popup-form">
            <tbody>
              <tr>
                <th>계좌명</th>
                <td><input readOnly value={data.account_name} className="popup-input readonly" /></td>
              </tr>
              <tr>
                <th>종목명</th>
                <td><input readOnly value={data.stock_name || data.ticker} className="popup-input readonly" /></td>
              </tr>
              <tr>
                <th>티커</th>
                <td><input readOnly value={data.ticker} className="popup-input readonly" /></td>
              </tr>
              <tr>
                <th>차수</th>
                <td><input readOnly value={turnLabel} className="popup-input readonly" /></td>
              </tr>
              <tr><td colSpan={2} className="popup-divider" /></tr>
              <tr>
                <th>매수일자 *</th>
                <td>
                  <input
                    type="date"
                    value={buyDate}
                    onChange={e => setBuyDate(e.target.value)}
                    className="popup-input"
                  />
                </td>
              </tr>
              <tr>
                <th>매수단가 *</th>
                <td>
                  <input
                    type="number"
                    value={buyPrice}
                    onChange={e => setBuyPrice(e.target.value)}
                    className="popup-input"
                    min="0"
                    step="any"
                    placeholder="현재가"
                  />
                </td>
              </tr>
              <tr>
                <th>수량 *</th>
                <td>
                  <input
                    type="number"
                    value={quantity}
                    onChange={e => setQuantity(e.target.value)}
                    className="popup-input"
                    min="0"
                    step="any"
                  />
                </td>
              </tr>
              <tr>
                <th>매수금액</th>
                <td>
                  <input readOnly value={buyAmt > 0 ? fAmt(buyAmt) : ''} className="popup-input readonly right" />
                </td>
              </tr>
              <tr>
                <th>목표수익률</th>
                <td>
                  <input
                    type="number"
                    value={targetRate}
                    onChange={e => setTargetRate(e.target.value)}
                    className="popup-input"
                    step="0.1"
                    placeholder="예: 10.00 (%)"
                  />
                </td>
              </tr>
              <tr>
                <th>메모</th>
                <td>
                  <input
                    value={memo}
                    onChange={e => setMemo(e.target.value)}
                    className="popup-input"
                    maxLength={200}
                    placeholder="선택 입력"
                  />
                </td>
              </tr>
            </tbody>
          </table>

          {error && <div className="popup-error">{error}</div>}
        </div>

        <div className="popup-footer">
          <button className="btn btn-primary" onClick={handleSave} disabled={saving || loadingTurn}>
            {saving ? '저장 중...' : '저장'}
          </button>
          <button className="btn btn-secondary" onClick={onClose} disabled={saving}>
            취소
          </button>
        </div>
      </div>
    </div>
  );
};

export default TurnBuyPopup;
